var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useState, useEffect } from "react";
import { httpClient } from '../services/http/httpClient';
import { aggsToSunburstData, createAggsViaSliceBy, isChartDataEmpty, generateFilterFromSunburstClick, removeSliceBySingles } from "./ChartUtils";
import Sunburst from "./Sunburst";
import Placeholder from "../general/Placeholder";
import { useEffectUpdate } from "../hooks/useEffectUpdate";
import { isEmptyObject, normaliseCaps } from "../general/Utils";
import { mergeFilters } from "../general/Filter";
function RemoteSunburst(props) {
    var endpoint = props.endpoint, sliceBy = props.sliceBy, baseUrl = props.baseUrl, height = props.height, legendPosition = props.legendPosition, noLabel = props.noLabel, noMini = props.noMini, filter = props.filter, setCombinedFilters = props.setCombinedFilters;
    var _a = useState({}), datasets = _a[0], setDatasets = _a[1];
    var _b = useState({}), miniDatasets = _b[0], setMiniDatasets = _b[1];
    var _c = useState(true), loading = _c[0], setLoading = _c[1];
    var _d = useState(true), miniLoading = _d[0], setMiniLoading = _d[1];
    var _e = useState(''), warningMessage = _e[0], setWarningMessage = _e[1];
    var _f = useState(''), errorMessage = _f[0], setErrorMessage = _f[1];
    var _g = useState({}), sliceData = _g[0], setSliceData = _g[1];
    var localFilter = generateFilterFromSunburstClick(sliceData);
    useEffect(function () {
        setLoading(true);
        var aggs = createAggsViaSliceBy(endpoint, sliceBy);
        httpClient().post('/' + endpoint + ":aggregations", aggs, {
            baseURL: baseUrl,
            params: {
                filter: filter
            }
        })
            .then(function (res) {
            var aggs = res.data.meta.aggregations;
            setErrorMessage('');
            setWarningMessage(isChartDataEmpty(aggs));
            var data = aggsToSunburstData(aggs, sliceBy);
            setDatasets(data);
            setLoading(false);
        })
            .catch(function (error) {
            setErrorMessage(error.message);
            console.error(error.message);
        });
    }, [filter]);
    // combine local and globalFilters
    useEffectUpdate(function () {
        if (setCombinedFilters !== undefined) {
            setCombinedFilters(mergeFilters(filter, localFilter));
        }
    }, [sliceData]);
    // reset localFilters when globalFilters are updated
    useEffectUpdate(function () {
        if (setCombinedFilters !== undefined) {
            setCombinedFilters(Object.assign({}, filter));
            setMiniDatasets({});
        }
    }, [filter]);
    // for mini sunburst updates
    useEffectUpdate(function () {
        // clear mini sunburst
        if (isEmptyObject(sliceData)) {
            setMiniDatasets({});
            // go deeper into the sunburst if not outer ring
        }
        else if (sliceData["datasetIndex"] !== 0) {
            setMiniLoading(true);
            var aggs = createAggsViaSliceBy(endpoint, removeSliceBySingles(sliceBy, sliceData["depth"]));
            httpClient().post('/' + endpoint + ":aggregations", aggs, {
                baseURL: baseUrl,
                params: {
                    filter: Object.assign({}, filter, localFilter)
                }
            })
                .then(function (res) {
                var aggs = res.data.meta.aggregations;
                setErrorMessage('');
                setWarningMessage(isChartDataEmpty(aggs));
                var data = aggsToSunburstData(aggs, sliceBy);
                setMiniDatasets(data);
                setMiniLoading(false);
            })
                .catch(function (error) {
                setErrorMessage(error.message);
                console.error(error.message);
            });
        }
    }, [sliceData]);
    if (errorMessage !== '') {
        return (_jsx(Placeholder, { errorMessage: errorMessage, height: height }));
    }
    if (warningMessage !== '') {
        return (_jsx(Placeholder, { warningMessage: warningMessage, height: height }));
    }
    if (loading) {
        return _jsx(Placeholder, { pie: true, height: height });
    }
    var miniActive = noMini === true ? false : !isEmptyObject(miniDatasets);
    var setter = (setCombinedFilters === undefined) ? undefined : setSliceData;
    return (_jsxs("div", __assign({ style: { height: height.toString() + 'px' } }, { children: [_jsx(Sunburst, __assign({}, props, { height: miniActive ? height * 0.25 : height, width: miniActive ? height * 0.5 : undefined, datasets: datasets, noLegend: miniActive, setSliceData: setter })), miniActive &&
                _jsx("div", { children: miniLoading ?
                        _jsx(Placeholder, { loader: true, clear: true, height: height * 0.75 })
                        :
                            _jsx(Sunburst, { title: normaliseCaps(sliceData["clickKey"]), height: height * 0.75, datasets: miniDatasets, legendPosition: legendPosition, noLabel: noLabel, noRefresh: true, setSliceData: setter }) })] })));
}
export default RemoteSunburst;
